"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { ShieldCheck, Activity, Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail, ArrowRight, CircleCheck, Cpu, Lock, Zap } from "lucide-react";
function NewsletterForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div data-section-id="36131" className="bg-primary/10 border border-primary/20 rounded-sm p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2">
        <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-serif uppercase tracking-wider text-primary">Access Granted</p>
          <p className="text-xs text-muted-foreground mt-1">{successMessage}</p>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-3">
      <FormField control={control} name="email" rules={{
      required: "Email is required for authorization.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid frequency format (email)."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                <Input placeholder="Join the Inner Circle..." className="pl-9 bg-background/50 border-border focus-visible:ring-primary/50 transition-all rounded-sm" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(var(--primary),0.3)] font-serif uppercase tracking-widest rounded-sm" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Activity className="mr-2 h-4 w-4 animate-spin" />
            Broadcasting...
          </> : <>
            Subscribe Now <ArrowRight className="ml-2 h-4 w-4" />
          </>}
      </Button>
      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5 font-mono">
        <Lock className="w-3 h-3 text-primary" />
        Exclusive Fort Lauderdale updates.
      </p>
    </Form>;
}
export default function SecurityIntelligenceFooter() {
  const currentYear = new Date().getFullYear();
  return <section id="footer-info" className="dark bg-background text-foreground border-t border-white/10 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {}
          <div className="space-y-6">
            <Link to="/ElboRoomHubPage" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587258/img/securecam_logo_white.png" alt="Elbo Room Hub" className="h-7 w-auto object-contain brightness-110" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-sans">
              The Elbo Room Hub is Fort Lauderdale's definitive source for live music, city events, and professional media coverage of the world-famous beach strip.
            </p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 transition-colors rounded-sm font-mono text-[10px]">
                <ShieldCheck className="w-3 h-3 mr-1" />
                ESTABLISHED 1938
              </Badge>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-serif uppercase tracking-wider flex items-center gap-2 text-foreground">
              <Zap className="w-4 h-4 text-primary" />
              The Hub
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/ElboRoomHubPage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 group-hover:bg-primary transition-colors" />
                  Live Show Schedule
                </Link>
              </li>
              <li>
                <Link to="/CityGuidePage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 group-hover:bg-primary transition-colors" />
                  FTL City Guide
                </Link>
              </li>
              <li>
                <Link to="/CorporatePage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 group-hover:bg-primary transition-colors" />
                  Corporate News
                </Link>
              </li>
              <li>
                <Link to="/ElboRoomHubPage" className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group font-sans">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 group-hover:bg-primary transition-colors" />
                  Beach Radio Hub
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-serif uppercase tracking-wider flex items-center gap-2 text-foreground">
              <MapPin className="w-4 h-4 text-primary" />
              Location
            </h3>
            <div className="space-y-4 text-sm text-muted-foreground font-sans">
              <div className="space-y-1">
                <p className="font-semibold text-foreground flex items-center gap-2 font-serif uppercase tracking-wide">
                  <span className="w-1 h-4 bg-primary rounded-sm" />
                  Beach Landmark
                </p>
                <p className="pl-3 border-l border-border ml-0.5">
                  241 S Fort Lauderdale Beach Blvd,<br />
                  Fort Lauderdale, FL 33316
                </p>
              </div>
              
              <div className="space-y-1">
                <p className="font-semibold text-foreground flex items-center gap-2 font-serif uppercase tracking-wide">
                  <span className="w-1 h-4 bg-chart-2 rounded-sm" />
                  Corporate HQ
                </p>
                <p className="pl-3 border-l border-border ml-0.5">
                  One Financial Plaza,<br />
                  Fort Lauderdale, FL 33394
                </p>
              </div>

              <div className="pt-2 space-y-2 font-mono text-xs">
                <Link to="tel:+19544634615" className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Phone className="w-4 h-4" />
                  +1 954 463 4615
                </Link>
                <Link to="mailto:info@elboroom.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Mail className="w-4 h-4" />
                  info@elboroom.com
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-serif uppercase tracking-wider flex items-center gap-2 text-foreground">
              <Cpu className="w-4 h-4 text-primary" />
              The Ticker
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Get the latest band schedules, city updates, and exclusive media events delivered to your inbox.
            </p>
            
            <div className="bg-card/50 border border-border p-4 rounded-sm backdrop-blur-md">
              <FormProvider formKey="footer_info_form_elbo_room_hub" formId="footer_info_form_elbo_room_hub" sectionName="security-intelligence-footer" defaultValues={{
              email: ""
            }} successMessage="You've been added to the Inner Circle.">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="border-t border-white/5 bg-card/10 backdrop-blur-xl">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            
            {}
            <div className="flex items-center gap-2 order-2 md:order-1">
              <div className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
              </div>
              <span className="text-xs font-mono text-primary tracking-widest uppercase">
                Live from the Strip
              </span>
            </div>

            {}
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground order-3 md:order-2 text-center md:text-left font-sans">
              &copy; {currentYear} Elbo Room. All rights reserved. <span className="hidden sm:inline">|</span> <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link> <span className="hidden sm:inline">|</span> <span className="opacity-50">Designed in FTL</span>
            </div>

            {}
            <div className="flex items-center gap-4 order-1 md:order-3">
              <Link to="#" className="text-muted-foreground hover:text-primary hover:drop-shadow-[0_0_8px_rgba(255,184,0,0.8)] transition-all" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </Link>
              <Link to="#" className="text-muted-foreground hover:text-primary hover:drop-shadow-[0_0_8px_rgba(255,184,0,0.8)] transition-all" aria-label="Twitter">
                <Twitter className="w-4 h-4" />
              </Link>
              <Link to="#" className="text-muted-foreground hover:text-primary hover:drop-shadow-[0_0_8px_rgba(255,184,0,0.8)] transition-all" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </Link>
              <Link to="#" className="text-muted-foreground hover:text-primary hover:drop-shadow-[0_0_8px_rgba(255,184,0,0.8)] transition-all" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}